import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Eye } from "lucide-react";
import { SECTIONS, type SectionKey } from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { hubFor } from "./catalogue/catalogueNav";

// Access keys: each group is the unit staff permissions will hang off later.
export const NAV_GROUPS: { id: string; label: string; keys: SectionKey[]; always?: boolean }[] = [
  { id: "work", label: "Work", keys: ["work", "staff-portal", "handbook"], always: true },
  { id: "people", label: "People", keys: ["customers", "marketing-lists", "tags", "duplicates", "enquiries"] },
  { id: "club", label: "Wine Club & Plans", keys: ["club", "cellar", "print", "site-check"] },
  { id: "money", label: "Money", keys: ["gocardless", "pending", "failed", "reconciliation"] },
  { id: "marketing", label: "Marketing & Growth", keys: ["growth", "journal", "ideas", "emails", "mailbox", "reviews", "outreach-plan"] },
  { id: "events", label: "Events", keys: ["events", "private-events"] },
  { id: "site", label: "Site & Settings", keys: ["team", "who-did-what", "content", "regions", "activity", "qa", "golive", "connections"] },
  { id: "projects", label: "Projects", keys: ["project-product-pages", "team-tasks", "newsletter"] },
  { id: "catalogue", label: "Catalogue", keys: ["stock-home", "buy", "wines-margins"] },
];
const STORE = "phw_admin_nav_collapsed";

export function visibleGroups(user: { menu_groups?: string[]; hidden_sections?: string[]; is_owner?: boolean } | null | false) {
  const allowed = user && !user.is_owner && user.menu_groups && user.menu_groups.length ? user.menu_groups : null;
  const hidden = user && !user.is_owner && user.hidden_sections?.length ? user.hidden_sections : [];
  const groups = allowed ? NAV_GROUPS.filter((g) => g.always || allowed.includes(g.id)) : NAV_GROUPS;
  return hidden.length ? groups.map((g) => ({ ...g, keys: g.keys.filter((k) => !hidden.includes(k)) })).filter((g) => g.keys.length) : groups;
}

export function canSeePath(user: { menu_groups?: string[]; hidden_sections?: string[]; is_owner?: boolean } | null | false, pathname: string) {
  const groups = visibleGroups(user);
  const section = SECTIONS.find((s) => pathname.startsWith(`/admin/${s.key}`));
  if (!section) return true;
  return groups.some((g) => g.keys.includes(hubFor(section.key) as SectionKey));
}

export function viewOnlyGroupFor(user: { view_only_groups?: string[]; is_owner?: boolean } | null | false, pathname: string) {
  if (!user || user.is_owner || !user.view_only_groups?.length) return null;
  const section = SECTIONS.find((s) => pathname.startsWith(`/admin/${s.key}`));
  const g = section && NAV_GROUPS.find((x) => x.keys.includes(hubFor(section.key) as SectionKey));
  return g && user.view_only_groups.includes(g.id) ? g : null;
}

export function firstAllowedPath(user: { menu_groups?: string[]; hidden_sections?: string[]; is_owner?: boolean } | null | false) {
  const g = visibleGroups(user)[0];
  return g ? `/admin/${g.keys[0]}` : "/";
}

function readCollapsed(): string[] { try { return JSON.parse(localStorage.getItem(STORE) || "[]"); } catch { return []; } }

export function GroupedNav({ counts, titles = {}, onNavigate, mobile = false }: { counts: Partial<Record<SectionKey, number>>; titles?: Partial<Record<SectionKey, string>>; onNavigate?: () => void; mobile?: boolean }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const groups = visibleGroups(user);
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);
  const section = SECTIONS.find((s) => pathname.startsWith(`/admin/${s.key}`));
  const activeKey = section ? hubFor(section.key) : undefined;
  const activeGroup = NAV_GROUPS.find((g) => g.keys.some((k) => k === activeKey))?.id;
  useEffect(() => { localStorage.setItem(STORE, JSON.stringify(collapsed)); }, [collapsed]);
  const toggle = (id: string) => setCollapsed((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const prefix = mobile ? "admin-mobile-nav" : "admin-nav";
  return (
    <>
      {groups.map((g) => {
        const open = !collapsed.includes(g.id) || g.id === activeGroup;
        const total = g.keys.reduce((n, k) => n + (counts[k] || 0), 0);
        return (
          <div key={g.id} className="mb-2" data-testid={`${prefix}-group-${g.id}`} data-access={g.id}>
            <button type="button" onClick={() => toggle(g.id)} className="flex w-full items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--paper)]/45 hover:text-[var(--paper)]/80" data-testid={`${prefix}-group-toggle-${g.id}`} aria-expanded={open}>
              <span className="flex-1 text-left">{g.label}</span>
              {user && !user.is_owner && user.view_only_groups?.includes(g.id) && <span className="mr-1 flex items-center gap-0.5 text-[8px] uppercase tracking-widest text-[var(--paper)]/50" title="View only — you can look but not change anything here"><Eye className="h-2.5 w-2.5" strokeWidth={1.5} />view</span>}
              {!open && total > 0 && <span className="min-w-[1.25rem] bg-[var(--wine)] px-1 py-0.5 text-center text-[10px] normal-case tracking-normal tabular-nums text-[var(--paper)]" data-testid={`${prefix}-group-count-${g.id}`}>{total}</span>}
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
            </button>
            {open && g.keys.map((key) => {
              const s = SECTIONS.find((x) => x.key === key)!;
              const Icon = s.icon;
              return (
                <NavLink key={key} to={`/admin/${key}`} onClick={onNavigate} className={({ isActive }) => `mb-0.5 flex items-center gap-3 px-3 ${mobile ? "py-3 text-base" : "py-2 text-sm"} transition-colors duration-150 ${isActive || activeKey === key ? "bg-[var(--paper)] text-[var(--ink)]" : "text-[var(--paper)]/75 hover:bg-white/10 hover:text-[var(--paper)]"}`} data-testid={`${prefix}-${key}`}>
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span className="flex-1">{s.label}</span>
                  {counts[key] ? <span title={titles[key]} className="min-w-[1.5rem] bg-[var(--wine)] px-1.5 py-0.5 text-center text-[10px] tabular-nums text-[var(--paper)]" data-testid={`${prefix}-count-${key}`}>{counts[key]}</span> : null}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
